import { useToast } from '@chakra-ui/react';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import { useAddGroupMembers } from 'features/user-groups/api/useAddGroupMembers';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useMemo } from 'react';
import { addMemberTeamsToCache } from 'features/members/utils/memberGroupsCache';
import { useOrgGroups } from 'features/user-group/user-group-list/api/useOrgGroups';

/**
 * Hook to handle adding a member to multiple teams
 */
export const useAddMemberToTeams = (
  selectedTeamIds: string[],
  onResetSelection: () => void,
  onSuccess?: (addedTeamIds: string[]) => void,
) => {
  const toast = useToast();
  const history = useHistory();
  const [drawerState, drawerDispatch] = useDrawerStateDispatch();
  const drawerData = drawerState?.data;
  const memberId = drawerData?.memberId || '';
  const addGroupMembersMutation = useAddGroupMembers();
  const queryClient = useQueryClient();
  const orgGroupsQuery = useOrgGroups();
  const orgGroupsData = orgGroupsQuery?.data;

  // Get organization ID from drawer data first (most reliable), fall back to URL
  const organizationId =
    drawerData?.organizationId ||
    new URLSearchParams(history.location.search).get('organization');

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    const groups = orgGroupsData?.data ?? [];
    groups.forEach((g) => {
      if (g?.id) {
        map.set(g.id, g.name ?? '');
      }
    });
    return map;
  }, [orgGroupsData]);

  const handleCloseDrawer = () => {
    onResetSelection();
    drawerDispatch(closeDrawer());
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (selectedTeamIds.length === 0 || !memberId) {
      return false;
    }

    try {
      // Add member to each team sequentially
      // The API supports adding up to 5 members at a time, but we're adding one member to multiple teams
      for (const teamId of selectedTeamIds) {
        await addGroupMembersMutation.mutateAsync({
          groupId: teamId,
          members: [memberId],
        });
      }

      // Update localStorage cache immediately so details/list UI updates without waiting for backend propagation.
      // (Keep best-effort; UI still updates via invalidations if cache is unavailable.)
      try {
        addMemberTeamsToCache({
          organizationId,
          memberId,
          teamIds: selectedTeamIds,
          groupNameById,
        });
      } catch {
        // Swallow cache issues; UI can still update via refetch.
      }

      // Invalidate queries to refetch member's teams and the main members list
      await queryClient.invalidateQueries({
        queryKey: ['memberTeams', memberId],
      });
      await queryClient.invalidateQueries(['organization/members']);

      toast({
        status: 'success',
        description: `User added to ${selectedTeamIds.length} ${
          selectedTeamIds.length === 1 ? 'group' : 'groups'
        }`,
      });

      // Call onSuccess to trigger teamsKey increment which forces API refetch
      if (onSuccess) {
        onSuccess([...selectedTeamIds]);
      }

      // Return true - drawer will close
      return true;
    } catch (error) {
      console.error('Error adding user to groups:', error);
      toast({
        status: 'error',
        description: 'Failed to add user to groups',
      });
      return false;
    }
  };

  return {
    handleSubmit,
    isSubmitting: addGroupMembersMutation.isLoading,
    handleCloseDrawer,
  };
};
